import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useMatch,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import type { ColumnVisibilityState } from "@tanstack/react-table";
import {
	Building01,
	Circle,
	Plus,
	Stars01,
	Ticket01,
} from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import { ErrorState } from "@/components/Molecules/EmptyState/ErrorState";
import {
	TicketsTableFilters,
	type TicketsTableFiltersState,
} from "@/components/Molecules/FilterDrawer/TicketsTableFilters";
import {
	BadgeOrEmptyCell,
	createAppColumnHelper,
	DateOrEmptyCell,
	FeaturedIconCell,
	LabelOrEmptyCell,
	PrimaryTextOrEmptyCell,
	SecondaryTextOrEmptyCell,
	TABLE_ICON_GUTTER_WIDTH_PX,
} from "@/components/Molecules/Table";
import {
	ActiveDrawerRow,
	useActiveDrawerRowId,
} from "@/components/Molecules/Table/active-drawer-row";
import { TableListEmptyState } from "@/components/Molecules/Table/TableListEmptyState";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { CreateTicketFromTextModal } from "@/components/Organisms/CreateTicketFromTextModal/CreateTicketFromTextModal";
import { CreateTicketModal } from "@/components/Organisms/CreateTicketModal/CreateTicketModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { useFeatureFlag } from "@/contexts/ConfigProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useCreatableSubOrganizations } from "@/hooks/use-creatable-sub-organizations";
import { useOrganizationHasSubOrganizations } from "@/hooks/use-organization-has-sub-organizations";
import { useSubOrganizationNameById } from "@/hooks/use-sub-organization-name-by-id";
import { useTableSettings } from "@/hooks/use-table-settings";
import { navigateDestinyDrawerEntity } from "@/lib/navigate-destiny-drawer-entity";
import { useDestinyDrawerRouteMatches } from "@/lib/use-destiny-drawer-route-matches";
import { authQueryOptions } from "@/services/auth";
import {
	type TicketDisplay,
	type TicketParams,
	ticketQueryOptions,
	useExportTickets,
} from "@/services/ticket";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { resolveIconComponent } from "@/utils/icon-catalog";
import { resolveStatusCategoryBadgeColor } from "@/utils/status-category-colors";
import { TicketPriorityColor, TicketTypeIcon } from "@/utils/tickets";
import { formatLongDate } from "@/utils/utils";

// ============================================================================
// TABLE SETTINGS CONSTANTS
// ============================================================================

const TABLE_KEY = "web.tickets";
/** Icon column pinned left of Number — not user-reorderable. */
const TICKET_ICON_COLUMN_ID = "ticketIcon";
const ticketColumnHelper = createAppColumnHelper<TicketDisplay>();

/** Primary visible columns — fixed product order. */
const VISIBLE_COLUMN_ORDER = [
	TICKET_ICON_COLUMN_ID,
	"ticketNumber",
	"name",
	"description",
	"statusName",
	"typeName",
	"priorityName",
	"subOrganizationName",
	"assignedTicketUser",
	"dueDate",
	"createdat",
	"updatedat",
	"id",
] as const;
const DEFAULT_COLUMN_ORDER = [...VISIBLE_COLUMN_ORDER];
const DEFAULT_COLUMN_VISIBILITY: ColumnVisibilityState = { id: false };
const DEFAULT_SORTING = [{ id: "ticketNumber", desc: true }];
const DEFAULT_PAGE_SIZE = 10;

/**
 * Keeps Icon → Number → Name → Description → Status → Type → Priority →
 * Sub-Organization → Assignee → Due Date → Created → Updated → ID.
 * Unknown/stale ids are dropped.
 */
const normalizeColumnOrder = (_columnOrder?: string[]) => {
	return [...DEFAULT_COLUMN_ORDER];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	subOrganization?: string[];
	status?: string[];
	type?: string[];
	priority?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	subOrganization: [],
	status: [],
	type: [],
	priority: [],
};

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/tickets",
)({
	component: Tickets,
	validateSearch: (search) => {
		return z
			.object({
				create: z.string().optional(),
			})
			.parse(search);
	},
});

function Tickets() {
	const navigate = useNavigate();
	const { matches, pathname } = useDestinyDrawerRouteMatches();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const workspaceMatch = useMatch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/workspace",
		shouldThrow: false,
	});
	const activeTicketId = useActiveDrawerRowId({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId",
		param: "ticketId",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets",
	});
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [isFromTextOpen, setIsFromTextOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (search.create === "ticket") {
			setIsOpen(true);
		}
	}, [search.create]);

	const [filters, setFilters] = useState<TicketsTableFiltersState>({
		subOrganization: [],
		status: [],
		type: [],
		priority: [],
		createdDateRange: null,
		lastUpdateDateRange: null,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["TicketNumber"],
		"OrderBy.Descending": true,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	const columnVisibility = useMemo(
		() => ({
			...DEFAULT_COLUMN_VISIBILITY,
			...tableSettings.columnVisibility,
		}),
		[tableSettings.columnVisibility],
	);

	const params = useParams({ strict: false });

	const userInfoQuery = useQuery(authQueryOptions.userInfo());

	const defaultOrgId =
		userInfoQuery.data?.organizationUserInfo?.[0]?.organization?.id ??
		"unknown";

	const currentOrgId =
		(params as { orgId?: string }).orgId ?? defaultOrgId ?? "unknown";

	const { hasSubOrganizations } =
		useOrganizationHasSubOrganizations(currentOrgId);

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev: TicketsTableFiltersState) => ({
				...prev,
				subOrganization: tableSettings.filters.subOrganization ?? [],
				status: tableSettings.filters.status ?? [],
				type: tableSettings.filters.type ?? [],
				priority: tableSettings.filters.priority ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		ticketNumber: ["TicketNumber"],
		name: ["Name"],
		description: ["Description"],
		statusName: ["Status.Name"],
		typeName: ["Type.Name"],
		priorityName: ["Priority.Name"],
		subOrganizationName: ["SubOrganization.Name"],
		dueDate: ["DueDate"],
		createdat: ["CreatedAt"],
		updatedat: ["UpdatedAt"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportTickets();
	const debouncedFilters = useDebounced(filters, 500);

	const filterConfig: FilterConfig = {
		// API accepts a single SubOrganizationId string; join sends one or CSV ids.
		subOrganization: { paramName: "SubOrganizationId", transform: "join" },
		status: { paramName: "TicketStatusIds", transform: "join" },
		type: { paramName: "TicketTypeIds", transform: "join" },
		priority: { paramName: "TicketPriorityIds", transform: "join" },
		"createdDateRange.start": {
			paramName: "CreatedAfter",
			transform: "dateStart",
		},
		"createdDateRange.end": {
			paramName: "CreatedBefore",
			transform: "dateEnd",
			dateStartField: "createdDateRange.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "UpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "UpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
	};

	const filterParams = useMemo(() => {
		return buildFilterParams(debouncedFilters, filterConfig);
	}, [debouncedFilters]);

	const ticketCount = useQuery(
		ticketQueryOptions.count({
			SearchTerm: searchTerm,
			OrganizationId: currentOrgId,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredTicketCount = useQuery(
		ticketQueryOptions.count({
			OrganizationId: currentOrgId,
		}),
	);

	const allTickets = useQuery(
		ticketQueryOptions.list(startIndex, pageSize, {
			SearchTerm: searchTerm,
			OrganizationId: currentOrgId,
			...filterParams,
			...sortParams,
		}),
	);

	const canCreateTickets = useCreatableSubOrganizations({
		actionName: "CreateTicket",
		organizationId: currentOrgId,
		entityType: "Ticket",
	});

	const ticketFromTextEnabled = useFeatureFlag("ticketFromText");

	const canCreateTicketsFromText = useCreatableSubOrganizations({
		actionName: "CreateTicketFromText",
		organizationId: currentOrgId,
		entityType: "Ticket",
		enabled: ticketFromTextEnabled,
	});

	const subOrganizationNameById = useSubOrganizationNameById(
		currentOrgId,
		!!currentOrgId && currentOrgId !== "unknown" && hasSubOrganizations,
	);

	const data = useMemo(() => {
		return allTickets.data ?? [];
	}, [allTickets.data]);

	const pageCount = useMemo(() => {
		if (!ticketCount.data) return 0;
		return Math.ceil(ticketCount.data / pageSize);
	}, [ticketCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allTickets.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = ticketCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					SearchTerm: searchTerm,
					OrganizationId: currentOrgId,
					...filterParams,
					...sortParams,
					columns: visibleColumns?.join(","),
				} as TicketParams & { columns?: string },
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("tickets"));
				},
			},
		);
	};

	const handleRowClick = (ticket: TicketDisplay) => {
		if (!ticket.id) return;
		navigateDestinyDrawerEntity({
			navigate,
			matches,
			pathname,
			entityParam: "ticketId",
			entityId: ticket.id,
			closedHref: `/web/${currentOrgId}/tickets/${ticket.id}/details`,
			closedSearch: { mode: "view" },
		});
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (searchValue: string) => {
		setSearchTerm(searchValue);
		setPageIndex(0);
		saveSearchTerm(searchValue);
	};

	const handleFilterChange = (newFilters: TicketsTableFiltersState) => {
		setFilters(newFilters);
		setPageIndex(0);
		saveFilters({
			subOrganization: newFilters.subOrganization,
			status: newFilters.status,
			type: newFilters.type,
			priority: newFilters.priority,
		});
	};

	const ticketColumns = useMemo(
		() =>
			ticketColumnHelper.columns([
				ticketColumnHelper.display({
					id: TICKET_ICON_COLUMN_ID,
					header: "",
					enableSorting: false,
					enableHiding: false,
					size: TABLE_ICON_GUTTER_WIDTH_PX,
					minSize: TABLE_ICON_GUTTER_WIDTH_PX,
					maxSize: TABLE_ICON_GUTTER_WIDTH_PX,
					meta: {
						isIconGutter: true,
						disableColumnReorder: true,
						rowFullViewAction: (row: unknown) => {
							const ticket = row as TicketDisplay;
							if (!ticket.id) return;
							navigate({
								to: "/web/$orgId/tickets/$ticketId/workspace",
								params: { orgId: currentOrgId, ticketId: ticket.id },
							});
						},
					},
					cell: () => <FeaturedIconCell icon={Ticket01} />,
				}),
				ticketColumnHelper.accessor(
					(row) => (row.ticketNumber != null ? `# ${row.ticketNumber}` : null),
					{
						id: "ticketNumber",
						header: t("main.tickets.table.columns.number"),
						enableSorting: true,
						minSize: 100,
						cell: () => <SecondaryTextOrEmptyCell />,
					},
				),
				ticketColumnHelper.accessor("name", {
					id: "name",
					header: t("main.tickets.table.columns.name"),
					enableSorting: true,
					meta: {
						headerProps: {
							isRowHeader: true,
						},
					},
					minSize: 300,
					cell: () => <PrimaryTextOrEmptyCell />,
				}),
				ticketColumnHelper.accessor("description", {
					id: "description",
					header: t("main.tickets.table.columns.description"),
					enableSorting: true,
					minSize: 250,
					cell: () => <SecondaryTextOrEmptyCell />,
				}),
				ticketColumnHelper.accessor("statusName", {
					id: "statusName",
					header: t("main.tickets.table.columns.status"),
					enableSorting: true,
					minSize: 130,
					cell: ({ row }) => (
						<BadgeOrEmptyCell
							type="pill-color"
							color={resolveStatusCategoryBadgeColor(
								row.original.statusCategoryIconColor,
							)}
							icon={
								resolveIconComponent(row.original.statusCategoryIcon ?? "") ??
								Circle
							}
						/>
					),
				}),
				ticketColumnHelper.accessor("typeName", {
					id: "typeName",
					header: t("main.tickets.table.columns.type"),
					enableSorting: true,
					minSize: 150,
					cell: ({ row }) => {
						const typeName = row.original.typeName ?? "";
						const Icon =
							resolveIconComponent(row.original.typeIcon ?? "") ??
							TicketTypeIcon(typeName);
						return <LabelOrEmptyCell icon={Icon} />;
					},
				}),
				ticketColumnHelper.accessor("priorityName", {
					id: "priorityName",
					header: t("main.tickets.table.columns.priority"),
					enableSorting: true,
					minSize: 120,
					cell: ({ row }) => (
						<BadgeOrEmptyCell
							type="modern"
							color={TicketPriorityColor(row.original.priorityName ?? "")}
						/>
					),
				}),
				ticketColumnHelper.accessor("subOrganizationId", {
					id: "subOrganizationName",
					header: t("main.tickets.table.columns.subOrganization"),
					enableSorting: true,
					minSize: 200,
					cell: ({ row }) => {
						const subOrganizationId = row.original.subOrganizationId;
						const name = subOrganizationId
							? subOrganizationNameById.get(subOrganizationId)
							: undefined;
						return (
							<LabelOrEmptyCell icon={Building01} primary={name ?? null} />
						);
					},
				}),
				ticketColumnHelper.accessor(
					(row) => {
						const assigneeFromUsers =
							row.assignedUsers?.[0]?.organizationUser?.firstName &&
							row.assignedUsers?.[0]?.organizationUser?.lastName
								? `${row.assignedUsers[0].organizationUser.firstName} ${row.assignedUsers[0].organizationUser.lastName}`
								: null;
						return row.assignedTicketUser?.trim() || assigneeFromUsers;
					},
					{
						id: "assignedTicketUser",
						header: t("main.tickets.table.columns.assignedUser"),
						enableSorting: false,
						minSize: 150,
						cell: () => <PrimaryTextOrEmptyCell />,
					},
				),
				ticketColumnHelper.accessor(
					(row) => (row.dueDate ? formatLongDate(row.dueDate) : null),
					{
						id: "dueDate",
						header: t("main.tickets.table.columns.dueDate"),
						enableSorting: true,
						minSize: 175,
						cell: () => <DateOrEmptyCell />,
					},
				),
				ticketColumnHelper.accessor(
					(row) => (row.createdAt ? formatLongDate(row.createdAt) : null),
					{
						id: "createdat",
						header: t("main.tickets.table.columns.created"),
						enableSorting: true,
						minSize: 175,
						cell: () => <DateOrEmptyCell />,
					},
				),
				ticketColumnHelper.accessor(
					(row) => (row.updatedAt ? formatLongDate(row.updatedAt) : null),
					{
						id: "updatedat",
						header: t("main.tickets.table.columns.lastUpdate"),
						enableSorting: true,
						minSize: 175,
						cell: () => <DateOrEmptyCell />,
					},
				),
				ticketColumnHelper.accessor("id", {
					id: "id",
					header: t("main.tickets.drawer.tabs.details.id"),
					enableSorting: false,
					minSize: 200,
					cell: ({ row }) =>
						row.original.id ? (
							<CopyableId value={row.original.id} />
						) : (
							<EmptyValue />
						),
				}),
			]),
		[currentOrgId, navigate, subOrganizationNameById, t],
	);

	const columns = hasSubOrganizations
		? ticketColumns
		: ticketColumns.filter((column) => column.id !== "subOrganizationName");

	const openCreateTicket = () => {
		setIsOpen(true);
		navigate({
			to: "/web/$orgId/tickets",
			params: { orgId: currentOrgId },
			search: { create: "ticket" },
		});
	};

	const headerActions = [
		...(ticketFromTextEnabled && canCreateTicketsFromText.canCreate
			? [
					{
						label: t("main.tickets.fromText.addButton"),
						icon: Stars01,
						color: "secondary" as const,
						onPress: () => setIsFromTextOpen(true),
					},
				]
			: []),
		...(canCreateTickets.canCreate
			? [
					{
						label: t("common.actions.add"),
						icon: Plus,
						color: "accent" as const,
						onPress: openCreateTicket,
					},
				]
			: []),
	];

	const hasActiveSearchOrFilters =
		searchTerm.trim().length > 0 || Object.keys(filterParams).length > 0;

	const ticketsEmptyState = allTickets.isError ? (
		<ErrorState
			title={t("main.tickets.table.errors.title")}
			description={t("main.tickets.table.errors.description")}
			actionLabel={t("common.actions.tryAgain")}
			onAction={handleRefresh}
		/>
	) : hasActiveSearchOrFilters ? (
		<TableSearchEmptyState titleKey="main.tickets.table.empty.noSearchResults" />
	) : (
		<TableListEmptyState
			title={t("main.tickets.table.empty.title")}
			description={t("main.tickets.table.empty.description")}
			actionLabel={canCreateTickets.canCreate ? t("common.actions.add") : undefined}
			onAction={canCreateTickets.canCreate ? openCreateTicket : undefined}
		/>
	);

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					ticketQueryOptions.list(nextStartIndex, pageSize, {
						SearchTerm: searchTerm,
						OrganizationId: currentOrgId,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}

		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					ticketQueryOptions.list(prevStartIndex, pageSize, {
						SearchTerm: searchTerm,
						OrganizationId: currentOrgId,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		currentOrgId,
		searchTerm,
		filterParams,
		sortParams,
		queryClient,
	]);

	// Workspace is a full second-level page — skip landing table chrome.
	if (workspaceMatch) {
		return <Outlet />;
	}

	return (
		<ActiveDrawerRow.Root rowId={activeTicketId}>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("main.tickets.table.title")}
						description={t("main.tickets.table.description")}
						actions={headerActions.length > 0 ? headerActions : undefined}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<ActiveDrawerRow.Table
					tableKey={TABLE_KEY}
					data={allTickets.isError ? [] : data}
					columns={columns}
					showHeaderPadding={false}
					zebraStriping
					isLoading={allTickets.isLoading}
					isFetching={allTickets.isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					emptyState={ticketsEmptyState}
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={ticketCount.data}
					totalRowCount={unFilteredTicketCount.data}
					sortFieldMapping={sortFieldMapping}
					defaultSortField="ticketNumber"
					onSortChange={({ fields, descending }) => {
						setSortParams({
							"OrderBy.Fields": fields,
							"OrderBy.Descending": descending,
						});
					}}
					filterLayout="bar"
					filterBarType="landing"
					filterPlaceholder={t("main.tickets.table.search.placeholder")}
					filterInputTestId="tickets-table-search-input"
					filterValue={searchTerm}
					onDebouncedFilterChange={handleDebouncedSearchChange}
					filtersTrailingContent={
						<TicketsTableFilters
							value={filters}
							onChange={handleFilterChange}
						/>
					}
					enableTableActions
					hasActiveFilters={Object.keys(filterParams).length > 0}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					tableOptionsPortalTarget={tableOptionsHost}
					enableColumnReordering
					onRowClick={handleRowClick}
					getRowId={(row) => row.id ?? ""}
					enableColumnResizing
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={columnVisibility}
					defaultColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
					settingsLoaded={settingsLoaded}
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
					// PageLayout already gaps header → body; drop FilterBar top pad so it isn't doubled.
					filterBarClassName="pt-0"
				/>
			</PageLayout>

			<CreateTicketModal
				isOpen={isOpen}
				setIsOpen={(o) => {
					setIsOpen(o);
					if (!o)
						navigate({
							to: "/web/$orgId/tickets",
							params: { orgId: currentOrgId },
						});
				}}
				handleClose={() => {
					setIsOpen(false);
					navigate({
						to: "/web/$orgId/tickets",
						params: { orgId: currentOrgId },
					});
				}}
				pageSize={pageSize}
				pageIndex={pageIndex}
			/>
			{ticketFromTextEnabled ? (
				<CreateTicketFromTextModal
					isOpen={isFromTextOpen}
					setIsOpen={setIsFromTextOpen}
					organizationId={currentOrgId}
				/>
			) : null}
			<Outlet />
		</ActiveDrawerRow.Root>
	);
}
